/**
 * DOM construction helpers.
 *
 * The UI builds its tree once at boot and mutates nodes afterwards, so these
 * exist to keep the building code readable rather than to abstract anything.
 * Nothing here allocates per frame.
 */

import { clamp01 } from '../core/math';

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Append children to `parent` and return it, so trees can be written inline. */
export function add<T extends Element>(parent: T, ...children: readonly Node[]): T {
  for (const c of children) parent.appendChild(c);
  return parent;
}

export function button(label: string, onClick: () => void, className = 'ui-btn'): HTMLButtonElement {
  const b = el('button', className, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

/**
 * A segmented fill bar. The segments are a CSS mask over a single scaled
 * element, so an update is one style write and the browser animates it.
 */
export interface Meter {
  readonly el: HTMLElement;
  set(v01: number): void;
}

export function meter(className = 'ui-meter'): Meter {
  const root = el('span', className);
  const fill = el('i');
  root.appendChild(fill);
  let last = -1;
  return {
    el: root,
    set(v: number): void {
      // Quantised so a slowly draining stat writes style a few dozen times
      // over its whole range instead of every frame.
      const q = Math.round(clamp01(v) * 48) / 48;
      if (q === last) return;
      last = q;
      fill.style.transform = `scaleX(${q})`;
    },
  };
}

/** Wrap inline SVG markup from `ui/items.ts` in a sized span. */
export function icon(markup: string, className = 'ui-icon'): HTMLElement {
  const wrap = el('span', className);
  wrap.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${markup}</svg>`;
  return wrap;
}

/** Seconds as `m:ss`, or `h:mm:ss` once past an hour. */
export function fmtTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** Restart a CSS animation that is already applied to `node`. */
export function replay(node: HTMLElement, className: string): void {
  node.classList.remove(className);
  // Forced reflow: only ever called on discrete events, never per frame.
  void node.offsetWidth;
  node.classList.add(className);
}
